'use client';

import { useEffect, useState } from 'react';
import { getCurrentTenantSlug } from './platform-domain';

/**
 * Cache de labels da plataforma (qualquer category - status, validation, etc).
 * Funciona em paralelo ao platform-toast.tsx mas para uso sincrono via hook
 * useState/useEffect. Carrega lazy na primeira chamada e invalida por TTL.
 *
 * Uso tipico em components sincronos como Badge:
 *
 *   const label = usePlatformLabel('status.appointment.PENDING', 'Pendente');
 *
 * Na primeira render mostra o fallback (sincronicamente). Apos o fetch
 * terminar, dispara re-render com o valor real do banco. Diferenca
 * visivel apenas no primeiro carregamento da sessao (TTL 5min).
 *
 * Para hosts canonicos (sem tenant), busca o catalogo global. Para
 * subdominio de tenant, busca o catalogo ja resolvido com overrides.
 */
interface PlatformLabelRow {
  key: string;
  value: string;
  category: string;
}

interface CatalogResponse {
  items: PlatformLabelRow[];
}

const CATALOG_URL = '/api/v1/platform/messages/label-catalog';
const CACHE_TTL_MS = 5 * 60 * 1000;

interface CacheEntry {
  catalog: Map<string, string>;
  fetchedAt: number;
}

const cacheByScope = new Map<string, CacheEntry>();
const inflightByScope = new Map<string, Promise<Map<string, string>>>();
const subscribers = new Set<() => void>();

function scopeKey(slug: string | null): string {
  return slug ?? 'global';
}

function notify() {
  subscribers.forEach((fn) => fn());
}

async function loadCatalog(tenantSlug: string | null): Promise<Map<string, string>> {
  const key = scopeKey(tenantSlug);
  const now = Date.now();
  const cached = cacheByScope.get(key);
  if (cached && now - cached.fetchedAt < CACHE_TTL_MS) {
    return cached.catalog;
  }
  const inflight = inflightByScope.get(key);
  if (inflight) return inflight;
  const promise = (async () => {
    try {
      const url = tenantSlug
        ? `${CATALOG_URL}?tenantSlug=${encodeURIComponent(tenantSlug)}`
        : CATALOG_URL;
      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) throw new Error(`http ${res.status}`);
      const data: CatalogResponse = await res.json();
      const map = new Map<string, string>();
      for (const item of data.items ?? []) {
        map.set(item.key, item.value);
      }
      cacheByScope.set(key, { catalog: map, fetchedAt: Date.now() });
      notify();
      return map;
    } finally {
      inflightByScope.delete(key);
    }
  })();
  inflightByScope.set(key, promise);
  return promise;
}

function interpolate(
  template: string,
  vars: Record<string, string | number | undefined> | undefined,
): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, name) => {
    const v = vars[name];
    if (v === undefined || v === null) return `{${name}}`;
    return String(v);
  });
}

/**
 * Hook React: retorna o label da chave usando cache em memoria. Retorna
 * o fallback enquanto o fetch nao terminou (primeira render da sessao).
 *
 * Use em components sincronos como Badge, dropdowns, summary cards onde
 * a label aparece em multiplas linhas e renderiza muitas vezes.
 *
 * Suporta interpolacao de variaveis no formato {nome} - util para titulos
 * dinamicos como "Visite {tenantName}" ou descricoes parametrizadas.
 */
export function usePlatformLabel(
  key: string,
  fallback: string,
  vars?: Record<string, string | number | undefined>,
): string {
  const [version, setVersion] = useState(0);
  const tenantSlug = typeof window !== 'undefined' ? getCurrentTenantSlug() : null;
  const scope = scopeKey(tenantSlug);

  useEffect(() => {
    const cached = cacheByScope.get(scope);
    if (cached) return; // ja tem valor
    // .catch evita "unhandled promise rejection" no console quando o
    // backend retorna 500 - o hook ja exibe o fallback enquanto cache
    // estiver vazio, sem precisar reagir ao erro aqui.
    void loadCatalog(tenantSlug).catch(() => {});
    const sub = () => setVersion((v) => v + 1);
    subscribers.add(sub);
    return () => {
      subscribers.delete(sub);
    };
  }, [scope, tenantSlug]);

  void version; // forca re-render quando catalog atualiza
  const cached = cacheByScope.get(scope);
  const template = cached?.catalog.get(key) ?? fallback;
  return interpolate(template, vars);
}

/**
 * Variante do `usePlatformLabel` para quando o component precisa resolver
 * VARIAS chaves dinamicas numa lista (ex: os botoes de acao do agendamento,
 * cuja quantidade varia por status). Chama-se UMA vez no topo do component
 * (respeitando as regras de hooks) e devolve um resolvedor sincrono que pode
 * ser usado dentro de `.map`. Mesma fonte/cache do `usePlatformLabel`.
 */
export function usePlatformLabelResolver(): (
  key: string,
  fallback: string,
  vars?: Record<string, string | number | undefined>,
) => string {
  const [version, setVersion] = useState(0);
  const tenantSlug = typeof window !== 'undefined' ? getCurrentTenantSlug() : null;
  const scope = scopeKey(tenantSlug);

  useEffect(() => {
    const cached = cacheByScope.get(scope);
    if (cached) return;
    void loadCatalog(tenantSlug).catch(() => {});
    const sub = () => setVersion((v) => v + 1);
    subscribers.add(sub);
    return () => {
      subscribers.delete(sub);
    };
  }, [scope, tenantSlug]);

  void version; // re-render quando o catalog atualiza
  return (key, fallback, vars) => {
    const cached = cacheByScope.get(scope);
    const template = cached?.catalog.get(key) ?? fallback;
    return interpolate(template, vars);
  };
}

/**
 * Invalida cache em memoria - operador editou label em /admin/messages e
 * quer ver efeito imediato sem aguardar 5min de TTL.
 */
export function invalidatePlatformLabels(): void {
  cacheByScope.clear();
  inflightByScope.clear();
  notify();
}
